import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from '../../assets/styles/YogaCourse/Form100.module.css';

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API_BASE = `${ENV_API_BASE_URL}/api`;

const DEFAULT_INCLUDED = [
  '12 Days & 11 Nights Accommodation ',
  'Yoga material',
  'Daily nutritious vegetarian, vegan meals',
  'Kirtan Night',
  'Full Body Massage',
  'Weekend excursions'
];

const DEFAULT_NOT_INCLUDED = [
  'Ayurvedic Panchakarma & Treatment',
  'Air-conditioner (On Additional Charges)',
  'Visa fee/Air fare/Taxi pick-up from Delhi, Haridwar'
];

const toOrdinal = (num) => {
  const n = Number(num);
  if (!n) return '';
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const mod10 = n % 10;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  const startLabel = `${toOrdinal(start.getDate())} ${start.toLocaleString('en-US', { month: 'short' })} ${start.getFullYear()}`;
  const endLabel = `${toOrdinal(end.getDate())} ${end.toLocaleString('en-US', { month: 'short' })} ${end.getFullYear()}`;
  return `${startLabel} To ${endLabel}`;
};

const groupByYear = (items) => {
  const groups = {};
  items.forEach((item) => {
    const year = new Date(item.startDate).getFullYear();
    if (!groups[year]) groups[year] = [];
    groups[year].push(item);
  });
  return groups;
};

const getStatusClass = (statusType) => {
  switch (statusType) {
    case 'success': return styles.statusSuccess;
    case 'warning': return styles.statusWarning;
    case 'primary': return styles.statusPrimary;
    default: return '';
  }
};

const Form100 = ({
  courseId,
  courseTitle,
  batches: initialBatches,
  included,
  notIncluded,
  toBringImage,
  includedTitle,
  notIncludedTitle,
  toBringTitle,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('');
  const [includedTab, setIncludedTab] = useState('included');
  const [batches, setBatches] = useState(initialBatches || []);

  useEffect(() => {
    setBatches(initialBatches || []);
  }, [initialBatches]);

  useEffect(() => {
    if (!courseId || (initialBatches && initialBatches.length)) return;
    const fetchBatches = async () => {
      try {
        const res = await axios.get(`${API_BASE}/courses/${courseId}/batches`);
        setBatches(res.data?.data || []);
      } catch (err) {
        console.error('Failed to load batches', err);
      }
    };
    fetchBatches();
  }, [courseId, initialBatches]);

  const grouped = useMemo(() => groupByYear(batches), [batches]);
  const years = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  useEffect(() => {
    if (!years.length) return;
    if (activeTab && years.includes(activeTab)) return;
    const currentYear = String(new Date().getFullYear());
    setActiveTab(years.includes(currentYear) ? currentYear : years[0]);
  }, [years, activeTab]);

  const rows = grouped[activeTab] || [];

  const includedItems = included && included.length ? included : DEFAULT_INCLUDED;
  const notIncludedItems = notIncluded && notIncluded.length ? notIncluded : DEFAULT_NOT_INCLUDED;
  const includedHeading = includedTitle || 'What does the course fees include?';
  const notIncludedHeading = notIncludedTitle || 'What is not included in the course fees?';
  const toBringHeading = toBringTitle || 'What to bring with you?';

  const handleBook = (batchId) => {
    if (!courseId || !batchId) return;
    navigate(`/Course-Booking-Form?course=${courseId}&batch=${batchId}`);
  };

  return (
    <div className={styles.container}>
      <div className="container-fluid px-3 px-sm-4 px-lg-5">

        {/* ── Header ── */}
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>
            Upcoming Dates For {courseTitle || 'Yoga Teacher Training'}
          </h1>
          <div className={styles.headerDivider}>
            <span className={styles.dividerLine}></span>
            <span>🕉️</span>
            <span className={styles.dividerLine}></span>
          </div>
        </div>

        {/* ── Year Tab Buttons ── */}
        <div className={styles.tabContainer}>
          {years.map((year) => (
            <button
              key={year}
              className={`${styles.tabButton} ${activeTab === year ? styles.tabButtonActive : styles.tabButtonInactive}`}
              onClick={() => setActiveTab(year)}
            >
              Dates Of {year}
            </button>
          ))}
        </div>

        {/* ── Table ── */}
        <div className={styles.tableWrapper} id="course-dates">

          {/* Table Header — hidden on mobile, shown on md+ */}
          <div className={`${styles.tableHeader} ${styles.tableHeaderDesktop}`}>
            <div className={styles.tableHeaderGrid}>
              <div className={`${styles.tableHeaderCell} text-center`}>
                <h5 className={styles.tableHeaderTitle}>Course Date</h5>
                <span className={styles.tableBadge}>Availability</span>
              </div>
              <div className={`${styles.tableHeaderCell} text-center`}>
                <h5 className={styles.tableHeaderTitle}>Course</h5>
                <span className={styles.tableBadge}>Prices</span>
              </div>
              <div className={styles.tableHeaderCell}>
                <div className={styles.accommHeader}>
                  <h5 className={styles.priceHeader}>Accommodation</h5>
                  <div className={styles.accommBadges}>
                    <span className={styles.tableBadge}>2 Sharing Room</span>
                    <span className={styles.tableBadge}>Private Room</span>
                  </div>
                </div>
              </div>
              <div className={`${styles.tableHeaderCell} text-center`}>
                <h5 className={styles.tableHeaderTitle}>Book Now</h5>
                <span className={styles.tableBadge}>Limited Offer</span>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className={styles.tableBody}>
            {rows.length === 0 && (
              <div className={`${styles.tableRow} text-center`}>
                <p className="mb-0">No batches available.</p>
              </div>
            )}

            {rows.map((batch, index) => (
              <div
                key={batch._id || index}
                className={`${styles.tableRow} ${index < rows.length - 1 ? styles.tableRowBorder : ''}`}
              >
                {/* ── Desktop layout (md+): single grid row ── */}
                <div className={styles.rowDesktop}>
                  {/* Date + Status */}
                  <div className={`${styles.rowCell} text-center`}>
                    <div className={styles.courseDate}>{formatDateRange(batch.startDate, batch.endDate)}</div>
                    <span className={`${styles.statusBadge} ${getStatusClass(batch.statusType)}`}>
                      {batch.statusLabel}
                    </span>
                  </div>

                  {/* Course Price */}
                  <div className={`${styles.rowCell} text-center`}>
                    <span className={styles.currentPrice}>{batch.priceSharedOld || '-'}</span>
                  </div>

                  {/* Accommodation Prices */}
                  <div className={styles.rowCell}>
                    <div className={styles.accommPrices}>
                      <div className={`${styles.priceContainer} text-center`}>
                        <span className={styles.currentPrice}>{batch.priceShared || '-'}</span>
                        <span className={styles.oldPrice}>{batch.priceSharedOld || ''}</span>
                      </div>
                      <div className={`${styles.priceContainer} text-center`}>
                        <span className={styles.currentPrice}>{batch.pricePrivate || '-'}</span>
                        <span className={styles.oldPrice}>{batch.pricePrivateOld || ''}</span>
                      </div>
                    </div>
                  </div>

                  {/* Book Button */}
                  <div className={`${styles.rowCell} text-center`}>
                    <button
                      className={styles.bookButton}
                      onClick={() => handleBook(batch._id)}
                      disabled={batch.availableSeats <= 0}
                    >
                      {batch.availableSeats <= 0 ? 'Fully Booked' : 'Book Now'}
                    </button>
                  </div>
                </div>

                {/* ── Mobile layout: stacked card ── */}
                <div className={styles.rowMobile}>
                  {/* Date + status */}
                  <div className={styles.mobileDate}>
                    <div className={styles.courseDate}>{formatDateRange(batch.startDate, batch.endDate)}</div>
                    <span className={`${styles.statusBadge} ${getStatusClass(batch.statusType)}`}>
                      {batch.statusLabel}
                    </span>
                  </div>

                  {/* Prices grid */}
                  <div className={styles.mobilePriceGrid}>
                    <div className={styles.mobilePriceBlock}>
                      <span className={styles.mobilePriceLabel}>Course Price</span>
                      <span className={styles.currentPrice}>{batch.priceSharedOld || '-'}</span>
                    </div>
                    <div className={styles.mobilePriceBlock}>
                      <span className={styles.mobilePriceLabel}>2 Sharing Room</span>
                      <div>
                        <span className={styles.currentPrice}>{batch.priceShared || '-'}</span>
                        {batch.priceSharedOld && <span className={styles.oldPrice}>{batch.priceSharedOld}</span>}
                      </div>
                    </div>
                    <div className={styles.mobilePriceBlock}>
                      <span className={styles.mobilePriceLabel}>Private Room</span>
                      <div>
                        <span className={styles.currentPrice}>{batch.pricePrivate || '-'}</span>
                        {batch.pricePrivateOld && <span className={styles.oldPrice}>{batch.pricePrivateOld}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Book button */}
                  <div className={styles.mobileBookBtn}>
                    <button
                      className={styles.bookButton}
                      onClick={() => handleBook(batch._id)}
                      disabled={batch.availableSeats <= 0}
                    >
                      {batch.availableSeats <= 0 ? 'Fully Booked' : 'Book Now'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── What's Included Section ── */}
        <div className={styles.includedSection}>
          <div className={styles.includedCard}>

            {/* Included Tabs */}
            <div className={styles.includedTabs}>
              {[
                { key: 'included',    label: '✓ What Is Included?' },
                { key: 'notIncluded', label: '⊗ What Is Not Included?' },
                { key: 'toBring',     label: '☰ What To Bring?' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`${styles.includedTabButton} ${includedTab === key ? styles.includedTabActive : styles.includedTabInactive}`}
                  onClick={() => setIncludedTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Included Content */}
            <div className={styles.includedContent}>
              {includedTab === 'included' && (
                <>
                  <h3 className={styles.includedTitle}>{includedHeading}</h3>
                  <div className={styles.includedGrid}>
                    {includedItems.map((item, index) => (
                      <div key={index} className={styles.includedItem}>
                        <div className={styles.includedItemContent}>
                          <span className={styles.includedIcon}>🕉️</span>
                          <span className={styles.includedText}>{item}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {includedTab === 'notIncluded' && (
                <>
                  <h3 className={styles.includedTitle}>{notIncludedHeading}</h3>
                  <div className={styles.includedGrid}>
                    {notIncludedItems.map((item, index) => (
                      <div key={index} className={styles.includedItem}>
                        <div className={styles.includedItemContent}>
                          <span className={styles.includedIcon}>🕉️</span>
                          <span className={styles.includedText}>{item}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {includedTab === 'toBring' && (
                <>
                  <h3 className={styles.includedTitle}>{toBringHeading}</h3>
                  <div>
                    {toBringImage ? (
                      <img
                        src={toBringImage}
                        alt="What to bring for Yoga Teacher Training"
                        style={{ width: '100%', maxWidth: '100%', height: 'auto', display: 'block' }}
                      />
                    ) : (
                      <p className="mb-0">Please contact us for the packing checklist.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Form100;